import { c as _c } from "react/compiler-runtime";
import { getOrgOnboardingsById, getRequestClient, postOrgOnboardings } from '@onefootprint/fetch-dashboard';
import { onboarding } from '@onefootprint/footprint-js';
import { IcoWarning16 } from '@onefootprint/icons';
import type { ApiError, CreateOnboardingResponse, PlaybookVersionNoGraph, PostOrgOnboardingsData, VaultKind } from '@onefootprint/request-types/dashboard';
import { Button, Tooltip } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { type Ref, useEffect, useImperativeHandle, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSession from '@/hooks/use-session';
import type { CreateOnboardingBody, RunFormValues } from '../onboarding.types';
import { buildOnboardingBody } from '../onboarding.utils';
import { ApiErrorAlert } from './api-error-alert';
import { RunResult } from './run-result';
type RunActionsProps = {
  ref?: Ref<RunActionsHandle>;
  savedFpId: string | null;
  entityKind: VaultKind;
  playbook: PlaybookVersionNoGraph;
  hasErrors: boolean;
  hasHiddenErrors: boolean;
  onRevealHiddenErrors: () => void;
  hasPendingChanges: boolean;
  versionId?: string;
  onClose: () => void;
};
type LastRun = {
  fpId: string;
  onboardingExternalId?: string;
};
export type RunActionsHandle = {
  run: () => void;
};
export const RunActions = t0 => {
  const $ = _c(73);
  const {
    ref,
    savedFpId,
    entityKind,
    playbook,
    hasErrors,
    hasHiddenErrors,
    onRevealHiddenErrors,
    hasPendingChanges,
    versionId,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "testMode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    authHeaders,
    isLive
  } = useSession();
  const form = useFormContext();
  const [lastRun, setLastRun] = useState(null);
  let t2;
  if ($[1] !== authHeaders) {
    t2 = {
      mutationFn: async id => {
        const {
          data
        } = await getOrgOnboardingsById({
          path: {
            id
          },
          headers: authHeaders
        });
        return data;
      }
    };
    $[1] = authHeaders;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const refetchMutation = useMutation(t2);
  let t3;
  if ($[3] !== authHeaders || $[4] !== refetchMutation) {
    t3 = {
      mutationFn: async body => {
        refetchMutation.reset();
        const client = getRequestClient({
          disableBodySnakeCaseConversion: true
        });
        const {
          data: data_0
        } = await postOrgOnboardings({
          body: body as unknown as PostOrgOnboardingsData['body'],
          client,
          headers: authHeaders
        });
        if (data_0.status === "incomplete" && data_0.continueOnboarding) {
          await onboarding.initialize({
            onboardingSessionToken: data_0.continueOnboarding.token,
            onComplete: () => refetchMutation.mutate(data_0.id)
          });
        }
        return data_0;
      }
    };
    $[3] = authHeaders;
    $[4] = refetchMutation;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const runMutation = useMutation(t3);
  let t4;
  if ($[6] !== form || $[7] !== hasPendingChanges || $[8] !== isLive || $[9] !== playbook || $[10] !== runMutation || $[11] !== savedFpId || $[12] !== t || $[13] !== versionId) {
    t4 = values => {
      if (!savedFpId) {
        return;
      }
      let body_0;
      try {
        body_0 = buildOnboardingBody({
          fpId: savedFpId,
          playbook,
          values,
          isLive,
          hasPendingChanges,
          versionId,
          now: new Date().toISOString()
        });
      } catch {
        form.setError("prerequisiteData", {
          message: t(_temp)
        });
        return;
      }
      runMutation.mutate(body_0, {
        onSettled: () => setLastRun({
          fpId: savedFpId,
          onboardingExternalId: values.onboardingExternalId
        })
      });
    };
    $[6] = form;
    $[7] = hasPendingChanges;
    $[8] = isLive;
    $[9] = playbook;
    $[10] = runMutation;
    $[11] = savedFpId;
    $[12] = t;
    $[13] = versionId;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleSubmit = t4;
  let t5;
  if ($[15] !== entityKind || $[16] !== hasErrors || $[17] !== hasHiddenErrors || $[18] !== savedFpId || $[19] !== t) {
    t5 = hasErrors && !hasHiddenErrors ? t(_temp2) : savedFpId ? undefined : t($_1 => $_1.runMode.run.needEntityHint[entityKind]);
    $[15] = entityKind;
    $[16] = hasErrors;
    $[17] = hasHiddenErrors;
    $[18] = savedFpId;
    $[19] = t;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const disabledTooltip = t5;
  const isRunning = runMutation.isPending;
  const isBlocked = isRunning || !!disabledTooltip;
  let t6;
  if ($[21] !== form || $[22] !== handleSubmit || $[23] !== hasHiddenErrors || $[24] !== onRevealHiddenErrors) {
    t6 = () => hasHiddenErrors ? onRevealHiddenErrors() : void form.handleSubmit(handleSubmit)();
    $[21] = form;
    $[22] = handleSubmit;
    $[23] = hasHiddenErrors;
    $[24] = onRevealHiddenErrors;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const run = t6;
  let t7;
  if ($[26] !== isBlocked || $[27] !== run) {
    t7 = () => ({
      run: () => {
        if (!isBlocked) {
          run();
        }
      }
    });
    $[26] = isBlocked;
    $[27] = run;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  useImperativeHandle(ref, t7);
  let t8;
  if ($[29] !== form.control) {
    t8 = {
      control: form.control,
      name: "onboardingExternalId"
    };
    $[29] = form.control;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const obExternalId = useWatch(t8);
  let t9;
  if ($[31] !== refetchMutation || $[32] !== runMutation) {
    t9 = () => {
      runMutation.reset();
      refetchMutation.reset();
    };
    $[31] = refetchMutation;
    $[32] = runMutation;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== savedFpId) {
    t10 = [savedFpId];
    $[34] = savedFpId;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  useEffect(t9, t10);
  const isReusingFpId = !!savedFpId && savedFpId === lastRun?.fpId;
  const showIdempotencyWarning = isReusingFpId && !!lastRun && (!obExternalId || lastRun.onboardingExternalId === obExternalId);
  const error = runMutation.error ?? refetchMutation.error;
  let t11;
  if ($[36] !== runMutation.error?.code || $[37] !== t) {
    t11 = runMutation.error?.code === "T122" ? t(_temp3) : undefined;
    $[36] = runMutation.error?.code;
    $[37] = t;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const errorHint = t11;
  const result = refetchMutation.data ?? runMutation.data;
  let t12;
  if ($[39] !== entityKind || $[40] !== error || $[41] !== errorHint || $[42] !== lastRun || $[43] !== result) {
    t12 = (!!error || result) && <>{!!error && <ApiErrorAlert error={error} hint={errorHint} />}{result && lastRun && <RunResult run={result} fpId={lastRun.fpId} entityKind={entityKind} />}</>;
    $[39] = entityKind;
    $[40] = error;
    $[41] = errorHint;
    $[42] = lastRun;
    $[43] = result;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== t) {
    t13 = t(_temp4);
    $[45] = t;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== onClose || $[48] !== t13) {
    t14 = <Button variant="secondary" onClick={onClose}>{t13}</Button>;
    $[47] = onClose;
    $[48] = t13;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== showIdempotencyWarning || $[51] !== t) {
    t15 = showIdempotencyWarning ? <Tooltip content={t(_temp5)}><IcoWarning16 color="warning" /></Tooltip> : null;
    $[50] = showIdempotencyWarning;
    $[51] = t;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  const t16 = !disabledTooltip;
  let t17;
  if ($[53] !== t) {
    t17 = t(_temp6);
    $[53] = t;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== isBlocked || $[56] !== isRunning || $[57] !== run || $[58] !== t17) {
    t18 = <Button onClick={run} loading={isRunning} disabled={isBlocked}>{t17}</Button>;
    $[55] = isBlocked;
    $[56] = isRunning;
    $[57] = run;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== disabledTooltip || $[61] !== t16 || $[62] !== t18) {
    t19 = <Tooltip disabled={t16} content={disabledTooltip}>{t18}</Tooltip>;
    $[60] = disabledTooltip;
    $[61] = t16;
    $[62] = t18;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== t15 || $[65] !== t19) {
    t20 = <div className="flex items-center gap-2">{t15}{t19}</div>;
    $[64] = t15;
    $[65] = t19;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== t14 || $[68] !== t20) {
    t21 = <div className="flex justify-between items-center w-full max-md:sticky max-md:bottom-0 max-md:bg-primary">{t14}{t20}</div>;
    $[67] = t14;
    $[68] = t20;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  let t22;
  if ($[70] !== t12 || $[71] !== t21) {
    t22 = <div className="flex flex-col gap-4 w-full p-1">{t12}{t21}</div>;
    $[70] = t12;
    $[71] = t21;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  return t22;
};
function _temp($) {
  return $.runMode.invalidJson;
}
function _temp2($_0) {
  return $_0.testForm.playbookHasErrors;
}
function _temp3($_2) {
  return $_2.runMode.run.alreadyOnboardedHint;
}
function _temp4($_3) {
  return $_3.close;
}
function _temp5($_4) {
  return $_4.runMode.run.idempotencyWarning;
}
function _temp6($_5) {
  return $_5.runMode.run.runTest;
}